import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PiArrowClockwiseLight, PiListBulletsLight, PiTrayLight } from 'react-icons/pi';
import { useAppDispatch } from '../store';
import { showFeedbackModal } from '../store/actions/UserFeedbackActions';
import Header from '../components/shared/TailwindComponents/Header';
import Table from '../components/shared/TailwindComponents/Table';
import Skeleton from '../components/shared/TailwindComponents/Skeleton';
import EmptyState from '../components/shared/TailwindComponents/EmptyState';
import MarketplaceGate from '../components/Marketplace/MarketplaceGate';
import LawyerProfileForm from '../components/Marketplace/LawyerProfileForm';
import LawyerVerificationPanel from '../components/Marketplace/LawyerVerificationPanel';
import { enumLabel } from '../components/Marketplace/marketplaceLabels';
import { LeadStatusBadge, UrgencyBadge, countryName, fmtDate } from '../components/Marketplace/marketplaceUi';
import { LawyerProfile, Lead, errorMessage, getMyLawyerProfile, listLeads } from '../services/api/marketplaceService';

const COLUMNS = ['Type', 'Jurisdiction', 'Urgency', 'Scope', 'Status', 'Received'];

/**
 * Lawyer side: incoming requests, redacted to the triage summary (progressive
 * disclosure - the client's text and documents unlock after engagement).
 */
const InboxContent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [profile, setProfile] = useState<LawyerProfile | null | undefined>(undefined);
  const [leads, setLeads] = useState<Lead[] | null>(null);

  useEffect(() => {
    getMyLawyerProfile()
      .then(setProfile)
      .catch(() => setProfile(null));
  }, []);

  const loadInbox = useCallback(() => {
    setLeads(null);
    listLeads('inbox')
      .then(setLeads)
      .catch((err) => {
        setLeads([]);
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'error',
          duration: 4000,
          message: errorMessage(err, t('Could not load the inbox')),
        }));
      });
  }, [dispatch, t]);

  // Keyed on having a profile, not the object: verification updates replace it.
  const hasProfile = Boolean(profile);
  useEffect(() => {
    if (hasProfile) loadInbox();
  }, [hasProfile, loadInbox]);

  const rows = useMemo(
    () =>
      (leads ?? []).map((l) => ({
        id: l.uuid,
        uuid: l.uuid,
        Type: <span className="text-fg-strong">{l.triage ? enumLabel(t, l.triage.issue_type) : t('Not triaged yet')}</span>,
        Jurisdiction: countryName(t, l.triage?.jurisdiction),
        Urgency: l.triage?.urgency ? <UrgencyBadge urgency={l.triage.urgency} /> : '—',
        Scope: l.triage?.scope_est ? (
          <span className="block truncate" title={l.triage.scope_est.summary}>{enumLabel(t, l.triage.scope_est.size)}</span>
        ) : '—',
        Status: <LeadStatusBadge status={l.status} audience="lawyer" />,
        Received: fmtDate(l.created_at),
      })),
    [leads, t]
  );

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="marketplace-inbox">
      <Header
        breadcrumbs={[
          { name: t('Marketplace'), href: '/marketplace', current: false },
          { name: t('Lawyer inbox'), href: '/marketplace/inbox', current: true },
        ]}
        actions={[
          { label: t('My requests'), onClick: () => history.push('/marketplace'), icon: <PiListBulletsLight className="w-4 h-4" />, variant: 'outline' },
          ...(profile
            ? [{ label: t('Refresh'), onClick: loadInbox, icon: <PiArrowClockwiseLight className="w-4 h-4" />, variant: 'outline' as const }]
            : []),
        ]}
      />
      {/* Until verified, the lawyer can browse but not propose: show what's left to do. */}
      {profile && profile.verification_status !== 'verified' && (
        <div className="max-h-[60%] shrink-0 overflow-y-auto custom-scrollbar-main px-6 pt-4 pb-2">
          <div className="max-w-3xl">
            <LawyerVerificationPanel
              onChange={(v) => setProfile((p) => (p ? { ...p, verification_status: v.status } : p))}
            />
          </div>
        </div>
      )}
      <div className="flex-1 overflow-hidden">
        {profile === undefined ? (
          <Skeleton variant="table" rows={6} columns={COLUMNS.length} />
        ) : !profile ? (
          <div className="details-canvas h-full overflow-y-auto custom-scrollbar-main">
            <div className="details-pad mx-auto w-full max-w-3xl px-6 py-6">
              <LawyerProfileForm onCreated={setProfile} />
            </div>
          </div>
        ) : leads === null ? (
          <Skeleton variant="table" rows={6} columns={COLUMNS.length} />
        ) : leads.length === 0 ? (
          <EmptyState
            icon={<PiTrayLight size={40} />}
            headline={t('No requests in your inbox')}
            subtext={t('Triaged requests that match the marketplace appear here.')}
          />
        ) : (
          <div className="h-full overflow-hidden" data-testid="marketplace-inbox-leads">
            <Table
              columns={COLUMNS}
              data={rows}
              onRowClick={(row: { uuid: string }) => history.push(`/marketplace/inbox/${row.uuid}`)}
              total_items={rows.length}
              total_showing_items={rows.length}
              showingLabel={t('requests')}
              storageKeyPrefix="marketplace-inbox"
              defaultColumnWidths={{ Type: 180, Jurisdiction: 120, Urgency: 110, Scope: 120, Status: 120, Received: 160 }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const MarketplaceInboxPage: React.FC = () => (
  <MarketplaceGate skeleton="table">{() => <InboxContent />}</MarketplaceGate>
);

export default MarketplaceInboxPage;
